import { RouteBasePaths } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ModuleName } from '@televet/shared-types/productPackages';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { setDefaultRedirect } from 'state/currentClinic/currentClinicSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'shared/providers/AuthProvider';

const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;

interface UseModularity {
  isSubscriptionActive: boolean;
  isRecapClinic: boolean;
  isRecapTrialSubscription: boolean;
  recapTrialTotalDays: number | null;
  recapTrialRemainingDays: number | null;
  modules: ModuleName[];
  loaded: boolean;
  hasModule: (module: ModuleName) => boolean;
  shouldSettingDisplayWithModules: (setting: ModuleSetting, modules: ModuleName[]) => boolean;
  shouldSidePanelItemDisplayWithModules: (item: ModuleSidePanelItem, modules: ModuleName[]) => boolean;
}

const useModularity = (): UseModularity => {
  const { currentClinic, isLoadingUserData } = useClinicUser();
  const { isAuthenticated, isLoggingOut } = useAuth();
  const defaultRedirect = useAppSelector((state) => state.currentClinic.defaultRedirect);
  const [modules, setModules] = useState<ModuleName[]>([]);
  const [loaded, setLoaded] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isLoadingUserData) {
      setModules([]);
      setLoaded(false);
      return;
    }

    setModules(
      currentClinic?.clinicModules?.map((clinicModule) => {
        return clinicModule.module.name as ModuleName;
      }) || [],
    );

    setLoaded(true);
  }, [currentClinic, isLoadingUserData]);

  const hasModule = useCallback(
    (module: ModuleName): boolean => {
      return modules.includes(module);
    },
    [modules],
  );

  useEffect(() => {
    if (!isLoggingOut && !isLoadingUserData) {
      const newDefaultRedirect = hasModule(ModuleName.AppointmentsRecap)
        ? RouteBasePaths.Appointments
        : RouteBasePaths.Conversations;
      if (newDefaultRedirect !== defaultRedirect) {
        dispatch(setDefaultRedirect(newDefaultRedirect));
      }
    }
  }, [defaultRedirect, dispatch, isLoadingUserData, hasModule, isLoggingOut, isAuthenticated]);

  const { currentSubscription, isSubscriptionActive } = useMemo(() => {
    const currentActiveSubscription = currentClinic?.productPackageSubscriptions.find(
      ({ endAt }) => endAt === null || new Date(endAt) > new Date(),
    );
    if (currentActiveSubscription) {
      return {
        currentSubscription: currentActiveSubscription,
        isSubscriptionActive: true,
      };
    }

    const recentSubscription = currentClinic?.productPackageSubscriptions.sort((a, b) => {
      return new Date(b.endAt ?? 0).getTime() - new Date(a.endAt ?? 0).getTime();
    })?.[0];

    return {
      currentSubscription: recentSubscription,
      isSubscriptionActive: false,
    };
  }, [currentClinic?.productPackageSubscriptions]);

  const { isRecapClinic, isRecapTrialSubscription, recapTrialTotalDays, recapTrialRemainingDays } = useMemo(() => {
    const { productPackage, startAt, endAt } = currentSubscription || {};

    const isRecapClinic = !!productPackage?.name.startsWith('Flow Recap');
    const isRecapTrialSubscription = productPackage?.name === 'Flow Recap Trial';

    let recapTrialTotalDays = null;
    let recapTrialRemainingDays = null;

    if (isRecapTrialSubscription && endAt) {
      recapTrialTotalDays = Math.ceil((new Date(endAt).getTime() - new Date(startAt).getTime()) / ONE_DAY_IN_MS);
      recapTrialRemainingDays = Math.ceil((new Date(endAt).getTime() - Date.now()) / ONE_DAY_IN_MS);
      if (recapTrialRemainingDays < 0) {
        recapTrialRemainingDays = 0;
      }
    }

    return {
      isRecapClinic,
      isRecapTrialSubscription,
      recapTrialTotalDays,
      recapTrialRemainingDays,
    };
  }, [currentSubscription]);

  return {
    isSubscriptionActive,
    isRecapClinic,
    isRecapTrialSubscription,
    recapTrialTotalDays,
    recapTrialRemainingDays,
    modules,
    hasModule,
    shouldSettingDisplayWithModules,
    shouldSidePanelItemDisplayWithModules,
    loaded,
  };
};

export enum ModuleSetting {
  // My Settings
  Profile = 'Profile',
  Notifications = 'Notifications',
  Preferences = 'Preferences',
  // Clinic Settings
  GeneralInfo = 'GeneralInfo',
  ClinicHours = 'ClinicHours',
  Team = 'Team',
  Subscription = 'Subscription',
  // Flow Settings
  Conversations = 'Conversations',
  Tasks = 'Tasks',
  WebsiteWidget = 'WebsiteWidget',
  TextMessaging = 'TextMessaging',
  Appointments = 'Appointments',
  Automations = 'Automations',
  ServiceReminders = 'ServiceReminders',
  Payments = 'Payments',
  Billing = 'Billing',
  SyncStatus = 'SyncStatus',
  // Otto Labs
  OttoLabs = 'OttoLabs',
}

const shouldSettingDisplayWithModules = (setting: ModuleSetting, modules: ModuleName[]): boolean => {
  let decision = false;
  switch (setting) {
    case ModuleSetting.Profile:
      decision = true;
      break;
    case ModuleSetting.Notifications:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Preferences:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.GeneralInfo:
      decision = true;
      break;
    case ModuleSetting.ClinicHours:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Team:
      decision = true;
      break;
    case ModuleSetting.Subscription:
      decision = modules.includes(ModuleName.AppointmentsRecap);
      break;
    case ModuleSetting.Conversations:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Tasks:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.WebsiteWidget:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.TextMessaging:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Appointments:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Automations:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.ServiceReminders:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Payments:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.Billing:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.SyncStatus:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSetting.OttoLabs:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    default:
      decision = false;
      break;
  }

  return decision;
};

export enum ModuleSidePanelItem {
  Calendar = 'Calendar',
  Search = 'Search',
  Payments = 'Payments',
  PhoneDialer = 'PhoneDialer',
  ContactZendesk = 'ContactZendesk',
  SuccessCenter = 'SuccessCenter',
}

const shouldSidePanelItemDisplayWithModules = (item: ModuleSidePanelItem, modules: ModuleName[]): boolean => {
  let decision = false;
  switch (item) {
    case ModuleSidePanelItem.Calendar:
      decision = modules.includes(ModuleName.AppointmentsStandard) || modules.includes(ModuleName.AppointmentsRecap);
      break;
    case ModuleSidePanelItem.Search:
      decision = modules.includes(ModuleName.AppointmentsStandard) || modules.includes(ModuleName.AppointmentsRecap);
      break;
    case ModuleSidePanelItem.Payments:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSidePanelItem.PhoneDialer:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSidePanelItem.ContactZendesk:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    case ModuleSidePanelItem.SuccessCenter:
      decision = modules.includes(ModuleName.AppointmentsStandard);
      break;
    default:
      decision = false;
      break;
  }
  return decision;
};

export { useModularity, ModuleName };
